import { useEffect, useState } from 'react';

import { Box, ButtonBase, CircularProgress, Paper } from '@mui/material';

import { Icon } from '@/components/atoms/Icon/Icon';
import { radius } from '@/theme/radius';

export interface PhotoAttachmentBubbleProps {
  documentId?: string;
  isSending?: boolean;
  isFailed?: boolean;
  getDocumentUrl?: (documentId: string) => Promise<string | undefined>;
  onOpen?: () => void;
}

const CARD_WIDTH = 180;
const IMAGE_HEIGHT = 116;

const Preview = ({ url, isSending }: { url?: string; isSending: boolean }) => {
  if (isSending) return <CircularProgress size={28} color='inherit' />;
  if (!url) return <Icon icon='images' fontSize={32} />;
  return (
    <Box
      component='img'
      src={url}
      alt='Photo you sent'
      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
};

/** PhotoAttachmentBubble shows a sent photo as itself, with no filename. */
export const PhotoAttachmentBubble = ({
  documentId,
  isSending = false,
  isFailed = false,
  getDocumentUrl,
  onOpen,
}: PhotoAttachmentBubbleProps) => {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (documentId && getDocumentUrl) getDocumentUrl(documentId).then(setUrl);
  }, [documentId, getDocumentUrl]);

  const canOpen = !!onOpen && !isSending;

  return (
    <Paper
      variant={isFailed ? 'chatBubbleFailed' : 'chatBubbleFilled'}
      sx={{ width: CARD_WIDTH, p: 0.75 }}
    >
      <Box
        component={canOpen ? ButtonBase : 'div'}
        aria-label={canOpen ? 'Open photo' : undefined}
        onClick={canOpen ? onOpen : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: IMAGE_HEIGHT,
          borderRadius: `${radius.md}px`,
          overflow: 'hidden',
          // The design's placeholder: a light ground with a coral spinner.
          bgcolor: 'custom.neutral.100',
          color: 'custom.coral.600',
        }}
      >
        <Preview url={url} isSending={isSending} />
      </Box>
    </Paper>
  );
};

PhotoAttachmentBubble.displayName = 'PhotoAttachmentBubble';
export default PhotoAttachmentBubble;
